import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Typography,
  Box,
  useTheme,
  Tooltip,
  useMediaQuery,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputBase,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  TrendingUp as LongIcon,
  TrendingDown as ShortIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Shield as ShieldIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import SymbolControlButton from './common/symbol-control-button';
import api, { TRADE, POSITION, ROE_GUARD, FAVORITES } from '../../infrastructure/config/api';

const canUseBlockControls = (user) => {
  return !!user?.id;
};

const openTradingView = (symbol) => {
  const tradingViewSymbol = `BINANCE:${String(symbol || '').toUpperCase()}.P`;
  window.open(
    `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(tradingViewSymbol)}`,
    '_blank',
    'noopener,noreferrer'
  );
};

const PnLValue = ({ value, showCurrency = true, variant = 'body2' }) => {
  const numericValue = Number(value);
  const safeValue = Number.isFinite(numericValue) ? numericValue : 0;
  const isPositive = safeValue > 0;
  const isZero = safeValue === 0;

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        color: isZero ? 'text.secondary' : (isPositive ? 'success.main' : 'error.main'),
        fontWeight: 'medium',
        whiteSpace: 'nowrap'
      }}
    >
      {!isZero && (isPositive ? (
        <ArrowUpwardIcon fontSize="inherit" sx={{ mr: 0.5 }} />
      ) : (
        <ArrowDownwardIcon fontSize="inherit" sx={{ mr: 0.5 }} />
      ))}
      <Typography variant={variant} component="span">
        {isPositive ? `+${safeValue.toFixed(2)}` : safeValue.toFixed(2)}
        {showCurrency && ' $'}
      </Typography>
    </Box>
  );
};

const formatNumber = (value, options = {}) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  return numericValue.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options
  });
};

const formatSigned = (value, suffix = '', decimals = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  const sign = numericValue > 0 ? '+' : '';
  return `${sign}${numericValue.toFixed(decimals)}${suffix}`;
};

const formatTime = (time) => {
  if (!time) return null;
  const date = new Date(time);
  return {
    full: format(date, 'yyyy-MM-dd HH:mm:ss'),
    relative: formatDistanceToNow(date, { addSuffix: true })
  };
};

const EntryTime = ({ time, isLong = true, variant = 'body2' }) => {
  const theme = useTheme();

  if (!time) return <Typography variant={variant} color="text.secondary">—</Typography>;

  const { full, relative } = formatTime(time);

  return (
    <Tooltip title={`${full} (${time})`} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          p: 0.5,
          borderRadius: 1,
          backgroundColor: isLong
            ? theme.palette.success[50]
            : theme.palette.error[50],
          color: isLong
            ? theme.palette.success.dark
            : theme.palette.error.dark,
        }}
      >
        {isLong ? (
          <LongIcon fontSize="small" sx={{ mr: 0.5 }} />
        ) : (
          <ShortIcon fontSize="small" sx={{ mr: 0.5 }} />
        )}
        <Typography variant={variant} component="span">
          {relative}
        </Typography>
      </Box>
    </Tooltip>
  );
};

const TradeCard = ({
  trade,
  onClose,
  onManage,
  onDcaSameSize,
  dcaLoading,
  isAdmin,
  onToggleRoeGuard,
  roeGuardUpdating,
  onToggleBreakeven,
  breakevenUpdating,
  isFavorite,
  onToggleFavorite,
  favoriteUpdating
}) => {
  const theme = useTheme();
  const isTiny = useMediaQuery('(max-width:480px)');
  const isLongActive = (trade.long?.size || 0) > 0;
  const isShortActive = (trade.short?.size || 0) > 0;
  const isInactive = !isLongActive && !isShortActive;
  const totalPnlValue = Number(trade.totalPnl);
  const totalPnl = Number.isFinite(totalPnlValue) ? totalPnlValue : 0;
  const totalSize = (trade.long?.size || 0) + (trade.short?.size || 0);
  const pnlPercentage = totalSize > 0 ? (totalPnl / totalSize) * 100 : 0;
  const activeSide = isLongActive && !isShortActive
    ? 'LONG'
    : isShortActive && !isLongActive
      ? 'SHORT'
      : isLongActive && isShortActive
        ? 'HEDGE'
        : null;
  const leverage = trade.long?.leverage || trade.short?.leverage || null;
  const entryPrice = activeSide === 'LONG'
    ? trade.long?.entryPrice
    : activeSide === 'SHORT'
      ? trade.short?.entryPrice
      : null;
  const entryTime = activeSide === 'LONG'
    ? trade.long?.entryTime
    : activeSide === 'SHORT'
      ? trade.short?.entryTime
      : null;
  const roeGuardExcluded = trade.roeGuard?.isExcluded === true;
  const breakevenEnabled = trade.roeGuard?.breakevenEnabled !== false;
  const breakevenOrderId = trade.roeGuard?.breakevenOrderId;
  const breakevenStopPrice = trade.roeGuard?.breakevenStopPrice;
  const breakevenLastError = trade.roeGuard?.breakevenLastError;
  const hedgeMinNotionalBlocked = String(trade.roeGuard?.lastAction || '').includes('AUTO_HEDGE_V1_MIN_NOTIONAL');
  const canDcaSameSize = hedgeMinNotionalBlocked && (activeSide === 'LONG' || activeSide === 'SHORT');

  const formatRelativeShort = (time) => {
    if (!time) return '—';
    const relative = formatDistanceToNow(new Date(time), { addSuffix: true });
    return relative
      .replace('about ', '')
      .replace('less than ', '')
      .replace('almost ', '')
      .replace(' minutes', 'm')
      .replace(' minute', 'm')
      .replace(' hours', 'h')
      .replace(' hour', 'h')
      .replace(' days', 'd')
      .replace(' day', 'd');
  };

  const getValueColor = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 'text.secondary';
    if (numericValue > 0) return 'success.main';
    if (numericValue < 0) return 'error.main';
    return 'text.secondary';
  };

  const renderSideDetails = (sideKey, label, chipColor) => {
    const side = trade[sideKey];
    const sizeValue = formatNumber(side?.size);
    const leverageValue = formatNumber(side?.leverage, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const roeValue = formatSigned(side?.roe, '%');
    const pnlValue = formatSigned(side?.pnl, ' USDT');
    const entryTime = side?.entryTime;
    const hasPosition = (side?.size || 0) > 0;

    if (!hasPosition) return null;

    return (
      <Box
        sx={{
          p: 1.25,
          borderRadius: 1.5,
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          height: '100%'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Chip size="small" label={label} color={chipColor} sx={{ fontWeight: 600, height: 22 }} />
          <Typography variant="caption" color="text.secondary">
            {hasPosition ? 'Active' : '—'}
          </Typography>
        </Box>
        <Grid container spacing={0.75}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Position Size</Typography>
            <Typography variant="body2">
              {sizeValue ? `${sizeValue} USDT` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">Leverage</Typography>
            <Typography variant="body2">
              {leverageValue ? `${leverageValue}x` : '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">ROE</Typography>
            <Typography variant="body2" sx={{ color: getValueColor(side?.roe) }}>
              {roeValue || '—'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">PnL</Typography>
            <Typography variant="body2" sx={{ color: getValueColor(side?.pnl) }}>
              {pnlValue || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Entry Time</Typography>
            <Box sx={{ mt: 0.25 }}>
              {entryTime ? (
                <EntryTime time={entryTime} isLong={label === 'LONG'} variant="caption" />
              ) : (
                <Typography variant="caption" color="text.secondary">—</Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        borderRadius: 3,
        borderLeft: `4px solid ${totalPnl >= 0 ? theme.palette.success.main : theme.palette.error.main}`,
        backgroundColor: theme.palette.background.paper,
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <CardContent sx={{
        p: 1.5,
        '&:last-child': { pb: 1.5 },
        opacity: isInactive ? 0.6 : 1
      }}>
        <Box
          display="flex"
          alignItems={isTiny ? 'flex-start' : 'center'}
          justifyContent="space-between"
          sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 0.5, columnGap: 1 }}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ flexWrap: 'wrap', minWidth: 0, flex: '1 1 auto' }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              color={isInactive ? 'text.secondary' : 'text.primary'}
            >
              {trade.symbol}
            </Typography>
            {leverage && (
              <Chip
                size="small"
                label={`${leverage}x`}
                sx={{ fontWeight: 600, height: 22 }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              OPEN
            </Typography>
            {isTiny && roeGuardExcluded && (
              <Chip
                size="small"
                label="OFF"
                color="warning"
                sx={{ fontWeight: 600, height: 22 }}
              />
            )}
            {isTiny && breakevenOrderId && (
              <Chip
                size="small"
                label="SL @ Entry"
                color="success"
                sx={{ fontWeight: 600, height: 22 }}
              />
            )}
          </Box>
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              flexWrap: 'wrap',
              justifyContent: isTiny ? 'flex-start' : 'flex-end',
              width: isTiny ? '100%' : 'auto'
            }}
          >
            <Tooltip title="Open chart" arrow>
              <IconButton
                size="small"
                onClick={() => openTradingView(trade.symbol)}
                aria-label={`Open ${trade.symbol} chart`}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isFavorite ? 'Unfavorite' : 'Favorite'} arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => onToggleFavorite?.(trade.symbol)}
                  disabled={favoriteUpdating}
                  color={isFavorite ? 'error' : 'default'}
                >
                  {favoriteUpdating
                    ? <CircularProgress size={18} />
                    : (isFavorite ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />)}
                </IconButton>
              </span>
            </Tooltip>
            {isAdmin && (
              <Tooltip
                title={roeGuardExcluded ? 'OFF' : 'Exclude from ROE Guard'}
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onToggleRoeGuard?.(trade.symbol, roeGuardExcluded)}
                    disabled={roeGuardUpdating?.[trade.symbol]}
                    color={roeGuardExcluded ? 'error' : 'default'}
                  >
                    {roeGuardUpdating?.[trade.symbol]
                      ? <CircularProgress size={18} />
                      : <BlockIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip
                title={
                  breakevenEnabled
                    ? 'Cancel SL = Entry automation'
                    : 'Enable SL = Entry when ROE >= 20%'
                }
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onToggleBreakeven?.(trade.symbol, breakevenEnabled)}
                    disabled={breakevenUpdating?.[trade.symbol]}
                    color={breakevenEnabled ? 'success' : 'default'}
                  >
                    {breakevenUpdating?.[trade.symbol]
                      ? <CircularProgress size={18} />
                      : <ShieldIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isTiny && roeGuardExcluded && (
              <Chip size="small" label="OFF" color="warning" sx={{ fontWeight: 600, height: 22 }} />
            )}
            {!isTiny && breakevenOrderId && (
              <Tooltip title={`Stop price: ${breakevenStopPrice || 'entry'}`} arrow>
                <Chip size="small" label="SL @ Entry" color="success" sx={{ fontWeight: 600, height: 22 }} />
              </Tooltip>
            )}
            {!isTiny && !breakevenEnabled && (
              <Chip size="small" label="BE OFF" color="default" sx={{ fontWeight: 600, height: 22 }} />
            )}
            {breakevenLastError && (
              <Tooltip title={breakevenLastError} arrow>
                <Chip size="small" label="BE ERR" color="error" sx={{ fontWeight: 700, height: 22 }} />
              </Tooltip>
            )}
            {hedgeMinNotionalBlocked && (
              <Tooltip title="Auto hedge failed: order notional < 5 USDT. Please handle manually." arrow>
                <Chip
                  size="small"
                  label="HEDGE <5$"
                  color="error"
                  sx={{ fontWeight: 700, height: 22 }}
                />
              </Tooltip>
            )}
            <SymbolControlButton symbol={trade.symbol} size="small" />
          </Box>
        </Box>

        <Box sx={{ mb: 0.75 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            color={totalPnl >= 0 ? 'success.main' : 'error.main'}
            lineHeight={1.1}
          >
            {totalPnl >= 0 ? '+' : ''}{totalPnl.toFixed(2)} USDT
          </Typography>
          <Typography
            variant="body2"
            color={totalPnl >= 0 ? 'success.main' : 'error.main'}
            sx={{ mt: 0.25 }}
          >
            ROE {pnlPercentage.toFixed(2)}%
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Total Size {formatNumber(totalSize) || '0.00'} USDT
          </Typography>
        </Box>

        <Grid container spacing={1} sx={{ mb: 1 }}>
          {isLongActive && (
            <Grid item xs={12} md={isShortActive ? 6 : 12}>
              {renderSideDetails('long', 'LONG', 'success')}
            </Grid>
          )}
          {isShortActive && (
            <Grid item xs={12} md={isLongActive ? 6 : 12}>
              {renderSideDetails('short', 'SHORT', 'error')}
            </Grid>
          )}
        </Grid>

        <Box sx={{ pt: 0.75, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Grid container spacing={0.75}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Entry</Typography>
              <Typography variant="body2">
                {entryPrice
                  ? parseFloat(entryPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })
                  : '—'}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Mark</Typography>
              <Typography variant="body2">
                {parseFloat(trade.currentPrice).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4
                })}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Total Size</Typography>
              <Typography variant="body2">
                {parseFloat(totalSize).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}{' '}
                <Typography component="span" variant="caption" color="text.secondary">USDT</Typography>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Time</Typography>
              <Typography variant="body2" color="text.secondary">
                {activeSide === 'HEDGE'
                  ? `L ${formatRelativeShort(trade.long?.entryTime)} · S ${formatRelativeShort(trade.short?.entryTime)}`
                  : formatRelativeShort(entryTime)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexDirection: isTiny ? 'column' : 'row' }}>
          {canDcaSameSize && (
            <Button
              fullWidth
              variant="contained"
              color="warning"
              onClick={onDcaSameSize}
              disabled={dcaLoading || isInactive}
              size="small"
              sx={{ minHeight: 36, fontWeight: 700 }}
            >
              {dcaLoading ? 'DCA...' : 'DCA Same'}
            </Button>
          )}
          <Button
            fullWidth
            variant="contained"
            color="error"
            onClick={onClose}
            disabled={isInactive}
            size="small"
            sx={{ minHeight: 36 }}
          >
            Close
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={onManage}
            size="small"
            sx={{ minHeight: 36 }}
          >
            Manage
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

// Import shared trading pairs

const matchesRoeThreshold = (trade, minRoe = null, maxRoe = null) => {
  if (!Number.isFinite(minRoe) && !Number.isFinite(maxRoe)) return true;

  const longRoe = Number(trade?.long?.roe);
  const shortRoe = Number(trade?.short?.roe);

  const checkValue = (value) => {
    if (!Number.isFinite(value)) return false;
    if (Number.isFinite(minRoe) && value <= minRoe) return false;
    if (Number.isFinite(maxRoe) && value >= maxRoe) return false;
    return true;
  };

  return checkValue(longRoe) || checkValue(shortRoe);
};

const normalizeActiveTrades = (rawTrades = []) => {
  return rawTrades
    .filter((trade) => {
      const longSize = trade.long?.size || 0;
      const shortSize = trade.short?.size || 0;
      return longSize > 0 || shortSize > 0;
    })
    .sort((a, b) => {
      const aPnl = (a.long?.pnl || 0) + (a.short?.pnl || 0);
      const bPnl = (b.long?.pnl || 0) + (b.short?.pnl || 0);
      return bPnl - aPnl;
    });
};

const RunningTradesTable = ({
  trades: externalTrades = null,
  onUpdate,
  setTotalPnl,
  currentUser,
  favoritesOnly = false,
  minRoe = null,
  maxRoe = null,
  title = 'Running Trades',
  emptyMessage = 'No running trades found'
}) => {
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalPnl, setTotalPnlState] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [openLongs, setOpenLongs] = useState(0);
  const [openShorts, setOpenShorts] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [roeGuardUpdating, setRoeGuardUpdating] = useState({});
  const [breakevenUpdating, setBreakevenUpdating] = useState({});
  const [favoriteSymbols, setFavoriteSymbols] = useState(new Set());
  const [favoritesLoading, setFavoritesLoading] = useState(true);
  const [favoriteUpdating, setFavoriteUpdating] = useState({});
  const [dcaUpdating, setDcaUpdating] = useState({});
  const [symbolQuery, setSymbolQuery] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const canManageBlock = canUseBlockControls(currentUser);
  const usesExternalTrades = Array.isArray(externalTrades);

  const handleRowClick = (trade) => {
    // Only show dialog if the trade has an active position
    if ((trade.long?.size || 0) > 0 || (trade.short?.size || 0) > 0) {
      setSelectedTrade(trade);
    }
  };

  const handleCloseDialog = () => {
    setSelectedTrade(null);
  };

  const handleConfirmClose = async () => {
    if (!selectedTrade) return;

    setIsClosing(true);
    try {
      await api.post(POSITION.CLOSE(selectedTrade.symbol));
      // Refresh the table data
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error closing position:', error);
    } finally {
      setIsClosing(false);
      handleCloseDialog(); // Always close the dialog
    }
  };

  const handlePositionClosed = (symbol) => {
    console.log(`Position closed for ${symbol}, refreshing data...`);
    setRefreshKey(prev => prev + 1);
  };

  const handleRoeGuardToggle = async (symbol, isExcluded) => {
    if (!canManageBlock || roeGuardUpdating[symbol]) return;

    setRoeGuardUpdating(prev => ({ ...prev, [symbol]: true }));
    try {
      const action = isExcluded ? 'include' : 'exclude';
      const { data } = await api.post(ROE_GUARD.EXCLUDE, { symbol, action });
      const nextExcluded = data?.data?.isExcluded ?? !isExcluded;
      setTrades(prev => prev.map(trade => (
        trade.symbol === symbol
          ? { ...trade, roeGuard: { ...(trade.roeGuard || {}), isExcluded: nextExcluded } }
          : trade
      )));
    } catch (error) {
      console.error('Error updating ROE Guard status:', error);
    } finally {
      setRoeGuardUpdating(prev => ({ ...prev, [symbol]: false }));
    }
  };

  const handleBreakevenToggle = async (symbol, isEnabled) => {
    if (!canManageBlock || breakevenUpdating[symbol]) return;

    setBreakevenUpdating(prev => ({ ...prev, [symbol]: true }));
    try {
      const action = isEnabled ? 'cancel' : 'enable';
      const { data } = await api.post(ROE_GUARD.BREAKEVEN, { symbol, action });
      const nextEnabled = data?.data?.breakevenEnabled ?? !isEnabled;
      setTrades(prev => prev.map(trade => (
        trade.symbol === symbol
          ? {
            ...trade,
            roeGuard: {
              ...(trade.roeGuard || {}),
              breakevenEnabled: nextEnabled,
              breakevenOrderId: data?.data?.breakevenOrderId || null,
              breakevenStopPrice: data?.data?.breakevenStopPrice || null,
              breakevenLastError: null
            }
          }
          : trade
      )));
    } catch (error) {
      console.error('Error updating breakeven SL:', error);
    } finally {
      setBreakevenUpdating(prev => ({ ...prev, [symbol]: false }));
    }
  };

  const fetchFavorites = async () => {
    setFavoritesLoading(true);
    try {
      const { data } = await api.get(FAVORITES.LIST);
      const symbols = data?.data?.symbols || data?.symbols || [];
      const normalized = symbols.map((symbol) => String(symbol || '').toUpperCase());
      setFavoriteSymbols(new Set(normalized));
    } catch (error) {
      console.error('Error fetching favorites:', error);
      setFavoriteSymbols(new Set());
    } finally {
      setFavoritesLoading(false);
    }
  };

  const handleFavoriteToggle = async (symbol) => {
    const normalizedSymbol = String(symbol || '').toUpperCase();
    if (!normalizedSymbol || favoriteUpdating[normalizedSymbol]) return;

    setFavoriteUpdating((prev) => ({ ...prev, [normalizedSymbol]: true }));
    try {
      const { data } = await api.post(FAVORITES.TOGGLE, { symbol: normalizedSymbol });
      const isFavorite = data?.data?.isFavorite;
      setFavoriteSymbols((prev) => {
        const next = new Set(prev);
        if (isFavorite) {
          next.add(normalizedSymbol);
        } else {
          next.delete(normalizedSymbol);
        }
        return next;
      });
    } catch (error) {
      console.error('Error toggling favorite:', error);
    } finally {
      setFavoriteUpdating((prev) => ({ ...prev, [normalizedSymbol]: false }));
    }
  };

  const handleDcaSameSize = async (symbol) => {
    const normalizedSymbol = String(symbol || '').toUpperCase();
    if (!normalizedSymbol || dcaUpdating[normalizedSymbol]) return;

    setDcaUpdating((prev) => ({ ...prev, [normalizedSymbol]: true }));
    try {
      const { data } = await api.post(POSITION.DCA_SAME_SIZE(normalizedSymbol));
      if (data?.success) {
        await fetchRunningTrades(false);
      }
    } catch (error) {
      console.error('Error executing same-size DCA:', error?.response?.data || error);
    } finally {
      setDcaUpdating((prev) => ({ ...prev, [normalizedSymbol]: false }));
    }
  };

  // Create an empty trade object for a symbol
  const fetchRunningTrades = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setRefreshing(true);

      if (usesExternalTrades) {
        if (typeof onUpdate === 'function') {
          await onUpdate();
        }
        return;
      }

      // Fetch running trades
      const { data } = await api.get(TRADE.RUNNING);

      if (data?.success) {
        setTrades(normalizeActiveTrades(data.data || []));
      }
    } catch (error) {
      console.error('Error fetching running trades:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!usesExternalTrades) return;
    setTrades(normalizeActiveTrades(externalTrades));
    setLoading(false);
  }, [usesExternalTrades, externalTrades]);

  // Fetch data on component mount and set up refresh interval
  useEffect(() => {
    fetchFavorites();
    if (usesExternalTrades) {
      setLoading(false);
      return undefined;
    }

    fetchRunningTrades();
    const interval = setInterval(fetchRunningTrades, 5000);
    return () => clearInterval(interval);
  }, [refreshKey, usesExternalTrades]);

  const visibleTrades = useMemo(() => {
    let nextTrades = trades;

    if (favoritesOnly) {
      if (favoriteSymbols.size === 0) return [];
      nextTrades = nextTrades.filter((trade) => favoriteSymbols.has(String(trade.symbol || '').toUpperCase()));
    }

    if (Number.isFinite(minRoe) || Number.isFinite(maxRoe)) {
      nextTrades = nextTrades.filter((trade) => matchesRoeThreshold(trade, minRoe, maxRoe));
    }

    const normalizedQuery = symbolQuery.trim().toUpperCase();
    if (normalizedQuery) {
      nextTrades = nextTrades.filter((trade) => String(trade.symbol || '').toUpperCase().includes(normalizedQuery));
    }

    return nextTrades;
  }, [trades, favoriteSymbols, favoritesOnly, minRoe, maxRoe, symbolQuery]);

  useEffect(() => {
    let nextTotalPnl = 0;
    let nextTotalSize = 0;
    let longCount = 0;
    let shortCount = 0;

    visibleTrades.forEach((trade) => {
      const longSize = trade.long?.size || 0;
      const shortSize = trade.short?.size || 0;
      const longPnl = trade.long?.pnl || 0;
      const shortPnl = trade.short?.pnl || 0;

      nextTotalPnl += longPnl + shortPnl;
      nextTotalSize += longSize + shortSize;

      if (longSize > 0) longCount++;
      if (shortSize > 0) shortCount++;
    });

    setTotalPnlState(nextTotalPnl);
    setTotalSize(nextTotalSize);
    setOpenLongs(longCount);
    setOpenShorts(shortCount);

    if (setTotalPnl && !favoritesOnly && !Number.isFinite(minRoe) && !Number.isFinite(maxRoe)) {
      setTotalPnl(nextTotalPnl);
    }
  }, [visibleTrades, setTotalPnl, favoritesOnly, minRoe, maxRoe]);

  if (loading && !refreshing) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const confirmDialog = (
    <Dialog
      open={!!selectedTrade}
      onClose={handleCloseDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        Close Position
      </DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Are you sure you want to close the position for {selectedTrade?.symbol}?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} disabled={isClosing}>
          Cancel
        </Button>
        <Button onClick={handleConfirmClose} color="error" disabled={isClosing} autoFocus>
          {isClosing ? 'Closing...' : 'Close Position'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{
      mt: 3,
      width: '100%',
      borderRadius: 2,
      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
      backgroundColor: '#1B1D20'
    }}>
      <Box
        sx={{
          p: 2.25,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: 'space-between',
          borderBottom: '1px solid #2A2D33',
          background: 'linear-gradient(90deg, #1F2227 0%, #1B1D20 100%)',
          color: '#E6E7EB'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit', letterSpacing: 0.2 }}>
            {title}
          </Typography>
          <Chip
            label={`${openLongs + openShorts} Open ${(openLongs + openShorts) === 1 ? 'Position' : 'Positions'}`}
            size="small"
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
              color: '#D3D7DE',
              fontWeight: 600,
              mr: 1
            }}
          />
          <Chip
            label={`${openLongs} Long`}
            size="small"
            sx={{
              backgroundColor: 'rgba(36, 188, 132, 0.18)',
              color: '#7EE3B2',
              fontWeight: 600
            }}
          />
          <Chip
            label={`${openShorts} Short`}
            size="small"
            sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.18)',
              color: '#F29A9A',
              fontWeight: 600
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: { xs: '100%', sm: 200, md: 220 },
            maxWidth: { xs: '100%', sm: 240, md: 260 },
            flex: { xs: '1 1 100%', lg: '0 0 240px' },
            px: 0.875,
            py: 0,
            height: 40,
            borderRadius: 999,
            border: '1px solid rgba(255, 255, 255, 0.08)',
            backgroundColor: 'rgba(255, 255, 255, 0.04)',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)'
          }}
        >
          <SearchIcon sx={{ color: '#9AA2AF', mr: 0.75, fontSize: 18 }} />
          <InputBase
            value={symbolQuery}
            onChange={(event) => setSymbolQuery(event.target.value)}
            placeholder="Search symbol"
            inputProps={{ 'aria-label': 'Search symbol' }}
            sx={{
              flex: 1,
              color: '#E6E7EB',
              fontSize: '0.9rem',
              '& input': {
                py: 0.5
              },
              '& input::placeholder': {
                color: '#9AA2AF',
                opacity: 1
              }
            }}
          />
          {symbolQuery && (
            <IconButton
              size="small"
              onClick={() => setSymbolQuery('')}
              sx={{
                color: '#9AA2AF',
                ml: 0.25,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', marginLeft: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="#C7CCD4">
              Total PnL:
            </Typography>
            <PnLValue value={totalPnl} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" color="#C7CCD4">
              Total Size:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, color: '#E6E7EB' }}>
              {totalSize.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}{' '}
              <Typography component="span" variant="caption" color="#9AA2AF">
                USDT
              </Typography>
            </Typography>
          </Box>
          <IconButton
            onClick={() => fetchRunningTrades(false)}
            disabled={refreshing}
            size="small"
            sx={{
              color: '#D3D7DE',
              transition: 'transform 0.3s',
              transform: refreshing ? 'rotate(360deg)' : 'none',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: isMobile ? 1.5 : 2 }}>
        {favoritesOnly && favoritesLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : visibleTrades.length > 0 ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(320px, 1fr))' },
              gap: 2
            }}
          >
            {visibleTrades.map((trade) => (
              <TradeCard
                key={trade.symbol}
                trade={trade}
                onClose={() => handleRowClick(trade)}
                onManage={() => navigate(`/position-history/${trade.symbol}`)}
                onDcaSameSize={() => handleDcaSameSize(trade.symbol)}
                dcaLoading={!!dcaUpdating[String(trade.symbol || '').toUpperCase()]}
                isAdmin={canManageBlock}
                onToggleRoeGuard={handleRoeGuardToggle}
                roeGuardUpdating={roeGuardUpdating}
                onToggleBreakeven={handleBreakevenToggle}
                breakevenUpdating={breakevenUpdating}
                isFavorite={favoriteSymbols.has(String(trade.symbol || '').toUpperCase())}
                onToggleFavorite={handleFavoriteToggle}
                favoriteUpdating={favoriteUpdating[String(trade.symbol || '').toUpperCase()]}
              />
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              boxShadow: theme.shadows[1]
            }}
          >
            <Typography color="text.secondary" gutterBottom>
              {emptyMessage}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fetchRunningTrades()}
              disabled={refreshing}
              startIcon={<RefreshIcon />}
              sx={{ mt: 1 }}
            >
              Refresh
            </Button>
          </Box>
        )}
      </Box>

      {confirmDialog}
    </Box>
  );
};

export default RunningTradesTable;
